import { DateFormat, formatDate, now } from "common";
import React from "react";
import { Partner, Purchase, PurchaseStatus, Transaction, TransactionType } from "types";
import shallow from "zustand/shallow";
import { useCollection } from "../../../../hooks/useCollection";
import { DateFilterRange } from "../../../../stores/reports/analyticsFilterStore";
import { usePartnerCommissionsFilterStore } from "../../../../stores/reports/partnerCommissionsFilterStore";
import { TableSkeleton } from "../../../Common/Skeleton/TableSkeleton";
import { PartnerReconciliationList } from "./PartnerReconciliationList";
import { PartnerStatementFilter } from "./PartnerStatementFilter";
import { PartnerStatementList } from "./PartnerStatementList";
import { useSeller } from "@/hooks/useAccount";
import { getDefaultLogoUrl } from "@/models/Seller";

export const PartnerStatement = ({ partner }: { partner: Partner }) => {
    const [from, to] = usePartnerCommissionsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const range = usePartnerCommissionsFilterStore((state) => state.getDateParams(DateFilterRange.ITEM_ARRIVAL_DATE));

    const { data: purchases, isLoading: isLoadingPurchases } = useCollection<Purchase>("purchases", {
        ...range,
        partner: partner.id,
        status: PurchaseStatus.COMMITTED,
    });

    const transactionTypes = [TransactionType.PARTNER_RECONCILE_AUTO, TransactionType.PARTNER_RECONCILE_MANUAL];

    const { data: transactions, isLoading: isLoadingTransactions } = useCollection<Transaction>("transactions", {
        "type[in]": transactionTypes.join(","),
        partners: partner.id,
        "meta.start": formatDate(from, DateFormat.DATE_TIME_ISO),
        "meta.end": formatDate(to, DateFormat.DATE_TIME_ISO),
    });

    const isLoadingStatement = isLoadingPurchases || isLoadingTransactions;

    return (
        <>
            <PartnerStatementFilter />
            <div className="space-y-6">
                <PrintCompanyProfile />
                <PrintPartnerCompanyProfile partner={partner} />
                {isLoadingStatement ? (
                    <TableSkeleton rows={4} columns={5} />
                ) : (
                    <PartnerStatementList purchases={purchases} partner={partner} />
                )}
                {isLoadingTransactions ? (
                    <TableSkeleton rows={4} columns={5} />
                ) : (
                    transactions.length > 0 && <PartnerReconciliationList transactions={transactions} />
                )}
                <PrintFooter />
            </div>
        </>
    );
};

export const PrintCompanyProfile = () => {
    const seller = useSeller();

    return (
        <div className="hidden print:block">
            <div className="flex justify-center pt-2">
                <div className="flex-col space-y-1 text-center">
                    <img alt={`Logo for ${seller.name}`} src={getDefaultLogoUrl(seller, "small")} />
                    <p className="font-bold">{seller.name}</p>
                    <p>{seller.streetAddress}</p>
                    <p>{seller.city}</p>
                </div>
            </div>
        </div>
    );
};

const PrintPartnerCompanyProfile = ({ partner }: { partner: Partner }) => {
    const [from, to] = usePartnerCommissionsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);

    return (
        <div className="hidden px-6 print:block">
            <div className="items-center p-1 pb-10">
                <p>{formatDate(now(), DateFormat.DAY_DATE)}</p>

                <div className="pt-2">
                    <p>{partner.name}</p>
                    <p>{partner.address}</p>
                    <p>{partner.phone}</p>
                    <p>{partner.email}</p>
                </div>
            </div>
            <span>Dear {partner.name}</span>:
            <p>
                Enclosed are the commissions for {formatDate(from, DateFormat.DATE_SHORT)} -{" "}
                {formatDate(to, DateFormat.DATE_SHORT)}, and a summary of guests referred by you.
            </p>
        </div>
    );
};

const PrintFooter = () => {
    const seller = useSeller();

    return (
        <div className="hidden space-y-2 px-6 pb-10 print:block">
            <p>
                Thank you very much for booking your guests with {seller.company ?? "us"}. We appreciate your support.
            </p>
            <p>Yours truly,</p>
            <p className="font-bold">{seller.name}</p>
        </div>
    );
};
